import { useState, useEffect, useRef } from "react"

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseTimestamp = (ts) => {
  if (!ts) return 0
  const parts = ts.split(":").map(Number)
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  return 0
}

const formatTime = (secs) => {
  const s = Math.floor(secs)
  const m = Math.floor(s / 60)
  const ss = s % 60
  return `${m}:${ss.toString().padStart(2, "0")}`
}

const severityColor = (severity) =>
  severity === "high"
    ? "#dc2626"
    : severity === "medium"
      ? "#b45309"
      : "#1d4ed8"

// ── VideoPlayer ───────────────────────────────────────────────────────────────
const VideoPlayer = ({ recordingUrl, duration, flaggedTimestamps = [] }) => {
  const videoRef = useRef(null)
  const timelineRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [totalDuration, setTotalDuration] = useState(0)
  const [hoveredMarker, setHoveredMarker] = useState(null)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [isMuted, setIsMuted] = useState(false)

  const rates = [0.5, 1, 1.5, 2]

  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const onTime = () => setCurrentTime(v.currentTime)
    const onDuration = () => setTotalDuration(v.duration)
    const onEnded = () => setIsPlaying(false)
    v.addEventListener("timeupdate", onTime)
    v.addEventListener("loadedmetadata", onDuration)
    v.addEventListener("ended", onEnded)
    return () => {
      v.removeEventListener("timeupdate", onTime)
      v.removeEventListener("loadedmetadata", onDuration)
      v.removeEventListener("ended", onEnded)
    }
  }, [])

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (isPlaying) {
      v.pause()
      setIsPlaying(false)
    } else {
      v.play()
      setIsPlaying(true)
    }
  }

  const seekTo = (secs) => {
    const v = videoRef.current
    if (!v) return
    v.currentTime = secs
    setCurrentTime(secs)
  }

  const handleTimelineClick = (e) => {
    const rect = timelineRef.current.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    seekTo(pct * totalDuration)
  }

  const cycleRate = () => {
    const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length]
    setPlaybackRate(next)
    if (videoRef.current) videoRef.current.playbackRate = next
  }

  const skipBy = (secs) => {
    const v = videoRef.current
    if (!v) return
    seekTo(Math.max(0, Math.min(totalDuration, v.currentTime + secs)))
  }

  const toggleMute = () => {
    const v = videoRef.current
    if (!v) return
    v.muted = !isMuted
    setIsMuted(!isMuted)
  }

  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0

  const markers = flaggedTimestamps
    .map((f) => ({
      ...f,
      seconds: parseTimestamp(f.timestamp),
      color: f.source === "gaze" ? "#b45309" : severityColor(f.severity),
    }))
    .filter((f) => totalDuration === 0 || f.seconds <= totalDuration)

  return (
    <div className="px-10 pb-8">
      {/* Video frame */}
      <div
        className="relative w-full bg-zinc-900"
        style={{ aspectRatio: "16/9" }}
      >
        {/* Placeholder */}
        <div className="absolute inset-0 grid grid-cols-2">
          <div
            className="flex items-center justify-center"
            style={{ borderRight: "1px solid rgba(255,255,255,0.08)" }}
          >
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-12 h-12 flex items-center justify-center"
                style={{
                  background: "rgba(99,102,241,0.2)",
                  border: "1px solid rgba(99,102,241,0.3)",
                }}
              >
                <span
                  className="text-sm font-bold"
                  style={{
                    color: "#a5b4fc",
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}
                >
                  AI
                </span>
              </div>
              <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                Interviewer
              </span>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div
                className="w-12 h-12 flex items-center justify-center"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="rgba(255,255,255,0.25)"
                >
                  <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                </svg>
              </div>
              <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                Candidate
              </span>
            </div>
          </div>
        </div>

        {/* Actual video */}
        {recordingUrl && (
          <video
            ref={videoRef}
            src={recordingUrl}
            className="absolute inset-0 w-full h-full"
            style={{
              objectFit: "cover",
              opacity: isVideoReady ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
            onCanPlay={() => setIsVideoReady(true)}
          />
        )}

        {/* Click to play/pause overlay */}
        <div className="absolute inset-0 cursor-pointer" onClick={togglePlay} />

        {/* Center play/pause button — fades out when playing */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-200"
          style={{ opacity: isPlaying ? 0 : 1 }}
        >
          <div
            className="w-14 h-14 flex items-center justify-center"
            style={{
              background: "rgba(0,0,0,0.45)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              borderRadius: "50%",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="white"
              style={{ marginLeft: 3 }}
            >
              <path d="M8 5v14l11-7L8 5z" />
            </svg>
          </div>
        </div>

        {/* Current time overlay */}
        <div className="absolute bottom-3 left-3 pointer-events-none">
          <span
            className="text-[11px] font-bold text-white px-2 py-0.5"
            style={{
              background: "rgba(0,0,0,0.55)",
              fontFamily: "'IBM Plex Mono', monospace",
            }}
          >
            {formatTime(currentTime)} / {formatTime(totalDuration)}
          </span>
        </div>
      </div>

      {/* Controls bar */}
      <div className="border border-zinc-200 border-t-0 bg-white px-5 pt-3 pb-4">
        {/* Timeline */}
        <div
          className="relative mb-1 py-2 cursor-pointer"
          ref={timelineRef}
          onClick={handleTimelineClick}
        >
          <div className="h-1 w-full bg-zinc-100 relative">
            <div
              className="h-full bg-zinc-800 transition-none"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 w-3 h-3 bg-zinc-900 border-2 border-white shadow"
              style={{
                left: `${progress}%`,
                transform: "translate(-50%, -50%)",
                borderRadius: "50%",
              }}
            />
          </div>

          {/* Flagged markers */}
          {markers.map((m, i) => {
            const pct =
              totalDuration > 0 ? (m.seconds / totalDuration) * 100 : 0
            return (
              <div
                key={i}
                className="absolute top-1/2"
                style={{
                  left: `${pct}%`,
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                }}
                onMouseEnter={() => setHoveredMarker(i)}
                onMouseLeave={() => setHoveredMarker(null)}
                onClick={(e) => {
                  e.stopPropagation()
                  seekTo(m.seconds)
                }}
              >
                <div
                  className="w-1.5 h-4 cursor-pointer"
                  style={{ background: m.color }}
                />
                {hoveredMarker === i && (
                  <div
                    className="absolute bottom-6 left-1/2 z-20 w-44 p-2"
                    style={{
                      transform: "translateX(-50%)",
                      background: "#18181b",
                      border: "1px solid #3f3f46",
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className="text-[10px] font-bold"
                        style={{
                          color: m.color,
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}
                      >
                        {m.timestamp}
                      </span>
                      <span
                        className="text-[9px] font-bold uppercase tracking-widest"
                        style={{ color: m.color }}
                      >
                        {m.source === "gaze" ? "Gaze" : "Speech"}
                      </span>
                    </div>
                    <p className="text-[11px] font-semibold text-white leading-snug">
                      {m.title}
                    </p>
                    <p className="text-[10px] text-zinc-400 leading-snug mt-0.5">
                      {m.description}
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Time labels */}
        <div className="flex justify-between mb-3">
          <span
            className="text-[10px] text-zinc-400"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {formatTime(currentTime)}
          </span>
          <span
            className="text-[10px] text-zinc-400"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {formatTime(totalDuration)}
          </span>
        </div>

        {/* Buttons row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Skip back 10s */}
            <button
              className="text-zinc-400 hover:text-zinc-700 transition-colors"
              onClick={() => skipBy(-10)}
              title="Back 10s"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
              </svg>
            </button>

            {/* Play/Pause */}
            <button
              className="w-8 h-8 flex items-center justify-center bg-zinc-900 hover:bg-zinc-700 transition-colors"
              style={{ borderRadius: 0 }}
              onClick={togglePlay}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
                {isPlaying ? (
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                ) : (
                  <path d="M8 5v14l11-7L8 5z" />
                )}
              </svg>
            </button>

            {/* Skip forward 10s */}
            <button
              className="text-zinc-400 hover:text-zinc-700 transition-colors"
              onClick={() => skipBy(10)}
              title="Forward 10s"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12.01 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z" />
              </svg>
            </button>
          </div>

          {/* Playback speed + Mute */}
          <div className="flex items-center gap-2">
            {/* Mute toggle */}
            <button
              onClick={toggleMute}
              className="text-zinc-400 hover:text-zinc-700 transition-colors"
              title={isMuted ? "Unmute" : "Mute"}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                {isMuted ? (
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                ) : (
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                )}
              </svg>
            </button>

            {/* Playback speed */}
            <button
              onClick={cycleRate}
              className="text-[11px] font-bold px-2.5 py-1 bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-colors"
              style={{
                borderRadius: 0,
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              {playbackRate}×
            </button>
          </div>
        </div>
      </div>

      {/* Marker legend */}
      <div className="flex items-center gap-5 mt-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
          Markers:
        </span>
        {[
          { color: "#dc2626", label: "High" },
          { color: "#b45309", label: "Medium / Gaze" },
          { color: "#1d4ed8", label: "Low" },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="w-1.5 h-3 shrink-0" style={{ background: color }} />
            <span className="text-[11px] text-zinc-500">{label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default VideoPlayer
